import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { ChevronDown, ChevronUp, Target, Zap, CheckCircle2, Brain, Lock, Unlock, Clock, Flame, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

interface Phase1ContextPanelProps {
  sessionId: string;
}

interface SessionContext {
  summary?: string;
  primary_tension?: string;
  primary_hypothesis?: string;
  success_criteria?: string[];
  decision_type?: string;
  // Phase 2 commitment data
  commit_mode?: boolean;
  // Phase 1.3 irreversibility data
  validation_deadline_days?: number;
  validation_metric?: string;
  validation_threshold?: string;
  daily_exposure?: number;
  structural_urgency_score?: number;
  pivot_cost_eur?: number;
}

export const Phase1ContextPanel = ({ sessionId }: Phase1ContextPanelProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [context, setContext] = useState<SessionContext | null>(null);

  useEffect(() => {
    loadContext();
  }, [sessionId]);

  const loadContext = async () => {
    try {
      const { data: session } = await supabase
        .from("decision_sessions")
        .select("summary, decision_type, meta")
        .eq("id", sessionId)
        .single();

      if (session) {
        const meta = (session.meta as Record<string, unknown>) || {};
        const irrev = (meta.phase1_3_irreversibility as Record<string, unknown>) || {};
        const conflict = (meta.phase1_2_conflict as Record<string, unknown>) || {};
        
        setContext({
          summary: session.summary || undefined,
          decision_type: session.decision_type || undefined,
          primary_tension: meta.primary_tension as string | undefined,
          primary_hypothesis: meta.primary_hypothesis as string | undefined,
          success_criteria: meta.success_criteria as string[] | undefined,
          commit_mode: meta.phase2_commit_mode === true,
          validation_deadline_days: (irrev.validation_deadline_days as number) || (meta.validation_deadline_days as number) || undefined,
          validation_metric: (irrev.validation_metric as string) || (meta.validation_metric as string) || undefined,
          validation_threshold: (irrev.validation_threshold as string) || (meta.validation_threshold as string) || undefined,
          daily_exposure: (irrev.exposure_until_validation as number) || (conflict.daily_exposure as number) || undefined,
          structural_urgency_score: irrev.structural_urgency_score as number | undefined,
          pivot_cost_eur: irrev.pivot_cost_eur as number | undefined,
        });
      }
    } catch (error) {
      console.error("Error loading context:", error);
    }
  };

  if (!context || (!context.summary && !context.primary_tension && !context.primary_hypothesis && !context.commit_mode)) {
    return null;
  }

  const hasPhase1Data = context.primary_hypothesis || context.primary_tension || (context.success_criteria && context.success_criteria.length > 0);
  const hasMetrics = context.validation_deadline_days || context.daily_exposure || context.validation_metric;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div className="relative overflow-hidden rounded-2xl border border-border/50 bg-card transition-all duration-500 hover:border-primary/20">
        <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full blur-3xl pointer-events-none" />
        
        <div className="relative z-10 p-5">
          <CollapsibleTrigger asChild>
            <Button variant="ghost" className="w-full flex items-center justify-between p-0 h-auto hover:bg-transparent group">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary/15 to-primary/5 flex items-center justify-center">
                  <Brain className="w-4 h-4 text-primary" />
                </div>
                <Badge className="bg-primary/10 text-primary border border-primary/20 text-xs">Phase 1 Kontext</Badge>
                
                {/* Commitment Status Badge */}
                {context.commit_mode !== undefined && (
                  <TooltipProvider delayDuration={200}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Badge 
                          variant="outline" 
                          className={cn(
                            "text-[10px] gap-1",
                            context.commit_mode 
                              ? "bg-primary/10 text-primary border-primary/30" 
                              : "bg-muted/50 text-muted-foreground border-border"
                          )}
                        >
                          {context.commit_mode ? <Lock className="h-2.5 w-2.5" /> : <Unlock className="h-2.5 w-2.5" />}
                          {context.commit_mode ? "Committed" : "Explorativ"}
                        </Badge>
                      </TooltipTrigger>
                      <TooltipContent>
                        {context.commit_mode 
                          ? "Phase-1-Commitment aktiv: Optionen müssen innerhalb der Wette bleiben"
                          : "Explorativer Modus: Keine Phase-1-Bindung"}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
                
                <span className="text-sm text-muted-foreground hidden sm:inline">
                  Deine Erkenntnisse aus der Analyse
                </span>
              </div>
              <div className="w-8 h-8 rounded-lg bg-muted/50 flex items-center justify-center group-hover:bg-primary/10 transition-colors">
                {isOpen ? (
                  <ChevronUp className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                )}
              </div>
            </Button>
          </CollapsibleTrigger>
          
          <CollapsibleContent className="mt-5">
            <div className="space-y-4">
              {/* Row 1: Phase 1 qualitative insights */}
              {hasPhase1Data && (
                <div className="grid gap-4 md:grid-cols-3">
                  {context.primary_hypothesis && (
                    <div className="group flex items-start gap-3 p-4 rounded-xl bg-gradient-to-br from-primary/5 to-transparent border border-primary/10 transition-all duration-300 hover:border-primary/20">
                      <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center shrink-0 group-hover:bg-primary group-hover:text-primary-foreground transition-all duration-300">
                        <Target className="h-4 w-4 text-primary group-hover:text-primary-foreground transition-colors" />
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-primary uppercase tracking-wide mb-1.5">Kernhypothese</p>
                        <p className="text-sm text-foreground leading-relaxed">{context.primary_hypothesis}</p>
                      </div>
                    </div>
                  )}
                  
                  {context.primary_tension && (
                    <div className="group flex items-start gap-3 p-4 rounded-xl bg-gradient-to-br from-amber-500/5 to-transparent border border-amber-500/10 transition-all duration-300 hover:border-amber-500/20">
                      <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-amber-500/20 to-amber-500/5 flex items-center justify-center shrink-0 group-hover:bg-amber-500 transition-all duration-300">
                        <Zap className="h-4 w-4 text-amber-600 dark:text-amber-400 group-hover:text-white transition-colors" />
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-amber-600 dark:text-amber-400 uppercase tracking-wide mb-1.5">Spannungsfeld</p>
                        <p className="text-sm text-foreground leading-relaxed">{context.primary_tension}</p>
                      </div>
                    </div>
                  )}
                  
                  {context.success_criteria && context.success_criteria.length > 0 && (
                    <div className="group flex items-start gap-3 p-4 rounded-xl bg-gradient-to-br from-emerald-500/5 to-transparent border border-emerald-500/10 transition-all duration-300 hover:border-emerald-500/20">
                      <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-emerald-500/20 to-emerald-500/5 flex items-center justify-center shrink-0 group-hover:bg-emerald-500 transition-all duration-300">
                        <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400 group-hover:text-white transition-colors" />
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-emerald-600 dark:text-emerald-400 uppercase tracking-wide mb-1.5">Erfolgskriterien</p>
                        <ul className="space-y-1">
                          {context.success_criteria.slice(0, 2).map((c, i) => (
                            <li key={i} className="text-sm text-foreground leading-relaxed">• {c}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Row 2: Quantitative metrics */}
              {hasMetrics && (
                <div className="grid gap-3 grid-cols-2 md:grid-cols-4">
                  {context.validation_deadline_days && (
                    <div className="flex items-center gap-2.5 p-3 rounded-lg bg-muted/30 border border-border/50">
                      <Clock className="h-4 w-4 text-primary shrink-0" />
                      <div>
                        <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Deadline</p>
                        <p className="text-sm font-semibold">{context.validation_deadline_days} Tage</p>
                      </div>
                    </div>
                  )}

                  {context.daily_exposure != null && context.daily_exposure > 0 && (
                    <div className="flex items-center gap-2.5 p-3 rounded-lg bg-muted/30 border border-border/50">
                      <Flame className="h-4 w-4 text-destructive shrink-0" />
                      <div>
                        <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Burn/Tag</p>
                        <p className="text-sm font-semibold">€{context.daily_exposure.toLocaleString()}</p>
                      </div>
                    </div>
                  )}

                  {context.validation_metric && (
                    <div className="flex items-center gap-2.5 p-3 rounded-lg bg-muted/30 border border-border/50 overflow-hidden min-w-0">
                      <Target className="h-4 w-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
                      <div className="min-w-0 overflow-hidden">
                        <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Validierung</p>
                        <p className="text-sm font-semibold truncate">{context.validation_metric}</p>
                        {context.validation_threshold && (
                          <p className="text-[10px] text-muted-foreground truncate">Schwelle: {context.validation_threshold}</p>
                        )}
                      </div>
                    </div>
                  )}

                  {context.pivot_cost_eur != null && context.pivot_cost_eur > 0 && (
                    <div className="flex items-center gap-2.5 p-3 rounded-lg bg-muted/30 border border-border/50 overflow-hidden min-w-0">
                      <XCircle className="h-4 w-4 text-destructive shrink-0" />
                      <div>
                        <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Pivot-Kosten</p>
                        <p className="text-sm font-semibold">€{context.pivot_cost_eur.toLocaleString()}</p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </CollapsibleContent>
        </div>
      </div>
    </Collapsible>
  );
};
